import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";

export const PLAN_RECOVERY_TIMEOUT = 4000;
const expiredMessage = "Your session ended. This draft cannot access Salesforce.";

const embeddedStyles = `
  body.deal-desk-embedded .app { --header: 52px; }
  body.deal-desk-embedded .app .topbar { height: var(--header); min-height: 0; padding: 6px 16px; }
  body.deal-desk-embedded .app .brand,
  body.deal-desk-embedded .plan-mobile-brand { display: none; }
  @media (max-width: 620px) {
    body.deal-desk-embedded .app { --header: 90px; }
    body.deal-desk-embedded .app .topbar { grid-template-columns: 32px minmax(0, 1fr); grid-template-rows: 38px 34px; gap: 4px 8px; padding: 6px 8px; }
    body.deal-desk-embedded .app .mobile-menu { grid-row: 1; grid-column: 1; width: 32px; height: 38px; }
    body.deal-desk-embedded .app .quick-add { grid-row: 1; grid-column: 2; width: 100%; height: 38px; }
    body.deal-desk-embedded .app .quick-add input { font-size: 16px; }
    body.deal-desk-embedded .app .quick-add .ib { display: inline-flex; flex-shrink: 0; }
    body.deal-desk-embedded .app .top-actions { grid-row: 2; grid-column: 1 / -1; justify-content: flex-end; width: 100%; min-width: 0; gap: 4px; }
    body.deal-desk-embedded .app .top-actions .ib,
    body.deal-desk-embedded .app .top-actions .top-action { width: 34px; min-width: 34px; height: 34px; justify-content: center; }
    body.deal-desk-embedded .app #layoutButton,
    body.deal-desk-embedded .app #guideButton,
    body.deal-desk-embedded .app #exportButton { display: inline-flex !important; }
  }
`;

// The document has an opaque origin. Cookies and API access stay in this parent.
const RiomaxPlanStudio = forwardRef(function RiomaxPlanStudio({ baseUrl = "", onClose, embedded = false }, ref) {
  const frame = useRef(null);
  const close = useRef(onClose);
  const control = useRef(null);
  const revokedLifetime = useRef(false);
  const recoverySequence = useRef(0);
  const [html, setHtml] = useState("");
  const [error, setError] = useState("");
  close.current = onClose;
  useImperativeHandle(ref, () => ({
    recover: () => control.current?.recover() || Promise.resolve(null),
    discard: () => control.current?.discard(),
  }), []);
  const frameHtml = useMemo(() => {
    if (!html || !embedded) return html;
    // Adapt only the host chrome; keep the protected source and its event handlers intact.
    const document = new DOMParser().parseFromString(html, "text/html");
    document.body.classList.add("deal-desk-embedded");
    const style = document.createElement("style");
    style.dataset.dealDesk = "embedded";
    style.textContent = embeddedStyles;
    document.head.appendChild(style);
    return `<!doctype html>${document.documentElement.outerHTML}`;
  }, [html, embedded]);

  useEffect(() => {
    const abort = new AbortController();
    let active = true;
    let revoked = revokedLifetime.current;
    let recovery = null;
    const pending = new Map();
    const expired = () => Object.assign(new Error(expiredMessage), { status: 401 });
    const revoke = () => {
      revokedLifetime.current = true;
      revoked = true;
      abort.abort();
      for (const send of pending.values()) send({ error: expiredMessage, status: 401 });
      pending.clear();
    };
    const dispose = () => {
      active = false;
      abort.abort();
      pending.clear();
      if (recovery) {
        clearTimeout(recovery.timer);
        recovery.reject(new Error("Draft recovery was cleared."));
        recovery = null;
      }
    };
    control.current = {
      discard() { revoke(); dispose(); },
      recover() {
        revoke();
        if (!active || !frame.current?.contentWindow) return Promise.resolve(null);
        if (recovery) return recovery.promise;
        const id = `recovery-${++recoverySequence.current}`;
        let resolve, reject;
        const promise = new Promise((done, fail) => { resolve = done; reject = fail; });
        const timer = setTimeout(() => {
          recovery = null;
          reject(new Error("Draft capture did not finish. Your draft is still open; retry before continuing."));
        }, PLAN_RECOVERY_TIMEOUT);
        recovery = { id, promise, resolve, reject, timer };
        frame.current.contentWindow.postMessage({ type: "plan:recovery-request", id }, "*");
        return promise;
      },
    };
    const api = async (path, options = {}, binary = false) => {
      if (!active || revoked) throw expired();
      const response = await fetch(`${baseUrl}/backend/sow/plan-studio${path}`, {
        ...options, signal: abort.signal, credentials: "include", cache: "no-store",
        headers: { "X-SOW-Project": "riomax", ...(options.body instanceof FormData ? {} : { "Content-Type": "application/json" }) },
      });
      if (!active || revoked) throw expired();
      if (response.status === 401) {
        revoke();
        window.dispatchEvent(new CustomEvent("sow:session-expired"));
        throw expired();
      }
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw Object.assign(new Error(payload.message || "Plan Studio could not complete this request."), { status: response.status });
      }
      const result = binary ? await response.blob() : path === "/content" ? await response.text() : (await response.json()).data;
      if (!active || revoked) throw expired();
      return result;
    };
    const receive = async (event) => {
      if (!active || event.source !== frame.current?.contentWindow || event.origin !== "null") return;
      const message = event.data;
      if (message?.type === "plan:recovery" && recovery && message.id === recovery.id) {
        const current = recovery;
        clearTimeout(current.timer);
        recovery = null;
        try {
          if (message.error) throw new Error("Draft capture failed. Your draft is still open; retry before continuing.");
          const data = message.data;
          if (data === null) { current.resolve(null); return; }
          if (!data || !Number.isSafeInteger(data.revision) || data.revision < 0 || !Array.isArray(data.workspace?.ideas)) {
            throw new Error("The draft could not be verified. Retry before continuing.");
          }
          // Only export the planning snapshot, never session state or arbitrary message fields.
          const json = JSON.stringify({ format: "riomax-plan-recovery-v1", revision: data.revision,
            workspace: data.workspace, drafts: data.drafts || {}, saved: false });
          if (new Blob([json]).size > 8 * 1024 * 1024) throw new Error("The draft is too large to capture. Keep this page open.");
          current.resolve(json);
        } catch (failure) { current.reject(failure); }
        return;
      }
      if (message?.type !== "plan:request" || typeof message.id !== "string" || message.id.length > 100 || pending.has(message.id)) return;
      const source = event.source;
      const send = (data) => {
        if (active && frame.current?.contentWindow === source) source.postMessage({ type: "plan:response", id: message.id, ...data }, "*");
      };
      if (revoked) { send({ error: expiredMessage, status: 401 }); return; }
      pending.set(message.id, send);
      try {
        let data;
        switch (message.operation) {
          case "load": data = await api(""); break;
          case "save": data = await api("", { method: "PUT", body: JSON.stringify(message.payload) }); break;
          case "upload": {
            const { ideaId, fileId, file, name } = message.payload || {};
            if (!(file instanceof Blob) || file.size > 10 * 1024 * 1024) throw new Error("Choose a file under 10 MB.");
            const body = new FormData(); body.append("file", file, String(name || "attachment"));
            data = await api(`/${encodeURIComponent(ideaId)}/files/${encodeURIComponent(fileId)}`, { method: "POST", body });
            break;
          }
          case "download": {
            const { ideaId, documentId } = message.payload || {};
            data = await api(`/${encodeURIComponent(ideaId)}/files/${encodeURIComponent(documentId)}/download`, {}, true);
            break;
          }
          case "close": close.current(); return;
          default: throw new Error("Unsupported planning request.");
        }
        if (pending.has(message.id)) send({ data });
      } catch (failure) {
        if (pending.has(message.id)) send({ error: failure.message, status: failure.status || 500 });
      } finally { pending.delete(message.id); }
    };
    window.addEventListener("message", receive);
    api("/content").then((value) => { if (active && !revoked) setHtml(value); }).catch((failure) => { if (active) setError(failure.message); });
    return () => { dispose(); control.current = null; window.removeEventListener("message", receive); };
  }, [baseUrl]);

  return <section className="sow-plan-studio" aria-label="Riomax Plan Studio">
    {html ? <iframe ref={frame} title="Riomax Plan Studio" srcDoc={frameHtml}
      sandbox="allow-scripts allow-forms allow-modals allow-downloads allow-popups allow-popups-to-escape-sandbox" />
      : <div role={error ? "alert" : "status"} className="sow-plan-studio-loading">
        <p>{error || "Opening Plan Studio..."}</p>
        {error && <button type="button" onClick={onClose}>Back to workspace</button>}
      </div>}
  </section>;
});

export default RiomaxPlanStudio;
